// Input: pointer-lock look, WASD/jump/sprint/sneak, hotbar select, flight toggle,
// inventory (E), pick-block (MMB), and mine/place via raycast (M0.4, M4). State
// lives on the Game; the fixed-tick loop consumes it (ENG-2). Gameplay input is
// suspended while a screen (chat/inventory) is open.
import * as THREE from 'three';
import { raycastVoxel } from '../sim/engine/raycast';
import { modeCaps } from '../sim/game/modes';
import { isEmpty, makeStack } from '../sim/game/inventory';
import { PLAYER } from '../sim/config';
import { actionForCode, hotbarSlot } from './keybinds';
import type { Game } from './boot';

const LOOK_SENS = 0.0022;
const HALF_PI = Math.PI / 2;

/**
 * Hangar §9.7 (M104.4, D-759 rule 2) — a reserved chord is forwarded to the shell only while
 * the pointer is unlocked: locked, TerraForge owns all input and the same key is player-facing
 * game input, not a shell request. `game.hangar` is unset outside `?hangar=1` (the NET-7
 * pattern), so this is a no-op there regardless of lock state. Exported for its own unit test;
 * `attachInput`'s listener is its only production caller.
 */
export function forwardHangarChord(game: Pick<Game, 'hangar' | 'locked'>, event: KeyboardEvent): boolean {
  if (!game.hangar || game.locked) return false;
  return game.hangar.handleKeyDown(event);
}

export function attachInput(game: Game): void {
  const canvas = game.renderer.domElement;
  const dir = new THREE.Vector3();
  // Interaction rays select any targetable block (shaped blocks included) so a
  // door/slab/chest can be mined, placed against, or used (M6).
  const isSolid = (x: number, y: number, z: number): boolean => game.world.isRaycastTarget(x, y, z);
  const pinv = () => game.playerState.inventory;

  const lookDir = (): { x: number; y: number; z: number } => {
    game.camera.getWorldDirection(dir);
    return dir;
  };

  canvas.addEventListener('click', () => {
    if (!game.locked && !game.uiOpen) canvas.requestPointerLock();
  });
  document.addEventListener('pointerlockchange', () => {
    game.locked = document.pointerLockElement === canvas;
    if (!game.locked) { game.breaking = false; game.usingItem = false; }
  });

  document.addEventListener('mousemove', (e) => {
    if (!game.locked || game.chatOpen || game.uiOpen) return;
    game.player.yaw -= e.movementX * LOOK_SENS;
    game.player.pitch -= e.movementY * LOOK_SENS * (game.settings.invertY ? -1 : 1);
    game.player.pitch = Math.max(-HALF_PI + 0.01, Math.min(HALF_PI - 0.01, game.player.pitch));
  });

  let lastSpace = 0;
  // F3 debug: F3 alone toggles the text overlay (on release, if no combo was used);
  // F3+G toggles chunk borders, F3+B toggles hitboxes — the Minecraft chords (M24).
  let f3Down = false, f3Combo = false;
  document.addEventListener('keydown', (e) => {
    if (forwardHangarChord(game, e)) return;
    if (e.code === 'F2') { e.preventDefault(); game.takeScreenshot(); return; } // capture works anytime (M24)
    if (e.code === 'F3') { e.preventDefault(); f3Down = true; f3Combo = false; return; }
    if (f3Down && e.code === 'KeyG') { e.preventDefault(); game.toggleChunkBorders(); f3Combo = true; return; }
    if (f3Down && e.code === 'KeyB') { e.preventDefault(); game.toggleHitboxes(); f3Combo = true; return; }
    if (game.chatOpen || game.uiOpen) return;
    if (e.code === 'Space') e.preventDefault();
    // Movement, jump, sneak/sprint, the hotbar slots, and the two menu keys all
    // route through the remappable binds (M24); the mouse buttons stay fixed.
    const act = actionForCode(game.settings.keybinds, e.code);
    switch (act) {
      case 'forward': game.input.forward = true; return;
      case 'back': game.input.back = true; return;
      case 'left': game.input.left = true; return;
      case 'right': game.input.right = true; return;
      case 'sneak': game.input.sneak = true; return;
      case 'sprint': game.input.sprint = true; return;
      case 'jump':
        game.input.jump = true;
        if (!e.repeat && modeCaps(game.playerState.gameMode).fly) {
          const now = performance.now();
          if (now - lastSpace < PLAYER.FLY_DOUBLE_TAP_MS) game.player.body.flying = !game.player.body.flying;
          lastSpace = now;
        }
        return;
      case 'inventory': if (game.locked) game.openPlayerInventory(); return;
      case 'advancements': if (game.locked) game.openAdvancements(); return;
      case 'lesson': if (game.locked) game.openLessonScreen(); return; // §29.3, M51.7 — KeyL stays advancements (D-TF-186)
      case 'handRaise': if (game.locked) game.eduRaiseHand(!game.eduHandUp()); return; // §29.8, D-TF-186
    }
    const slot = hotbarSlot(act);
    if (slot >= 0) pinv().selected = slot;
  });
  document.addEventListener('keyup', (e) => {
    if (e.code === 'F3') { if (!f3Combo) game.toggleDebugOverlay(); f3Down = false; return; }
    switch (actionForCode(game.settings.keybinds, e.code)) {
      case 'forward': game.input.forward = false; return;
      case 'back': game.input.back = false; return;
      case 'left': game.input.left = false; return;
      case 'right': game.input.right = false; return;
      case 'sneak': game.input.sneak = false; return;
      case 'sprint': game.input.sprint = false; return;
      case 'jump': game.input.jump = false; return;
    }
  });

  document.addEventListener('wheel', (e) => {
    if (!game.locked || game.uiOpen) return;
    pinv().selected = (pinv().selected + (e.deltaY > 0 ? 1 : -1) + 9) % 9;
  });
  document.addEventListener('contextmenu', (e) => e.preventDefault());

  document.addEventListener('mousedown', (e) => {
    if (!game.locked || game.chatOpen || game.uiOpen) return;
    if (e.button === 0) {
      // Left: a mob under the crosshair is attacked (PLR-3) in preference to mining.
      if (game.attack()) return;
      // Otherwise creative breaks instantly; survival holds to break (tickMining).
      game.breaking = true;
      if (modeCaps(game.playerState.gameMode).instantBreak) {
        const hit = raycastVoxel(isSolid, game.camera.position, lookDir(), game.reach);
        if (hit) game.mineBlock(hit.x, hit.y, hit.z);
        game.breaking = false;
      }
      return;
    }
    const hit = raycastVoxel(isSolid, game.camera.position, lookDir(), game.reach);
    if (e.button === 1) { // middle: pick block (creative clone / survival select)
      if (hit) pickBlock(game, game.world.getBlockState(hit.x, hit.y, hit.z));
      return;
    }
    // Right-hold begins here; hold-to-eat progresses in the tick (M11.2).
    if (e.button === 2) game.usingItem = true;
    // A mob under the crosshair is interacted with first (tame/sit/shear, MOB-2).
    if (e.button === 2 && game.useEntity()) { game.usingItem = false; return; }
    // Buckets act on the fluid/solid under the crosshair (their own fluid-aware
    // ray), so they work over open water even with no solid block hit (M9.1).
    if (e.button === 2 && game.useBucket()) return;
    // An empty map fills wherever the player stands (no block hit needed, M18).
    if (e.button === 2 && game.useMapInHand()) return;
    // A firework rocket boosts an active elytra glide, mid-air (no block hit, M21).
    if (e.button === 2 && game.useFirework()) return;
    // A field notebook records a genetic crop (its own raycast) or opens the
    // screen — works with or without a block hit (HER-7, §28.11). A mob is
    // already handled above, by `useEntity`.
    if (e.button === 2 && game.useFieldNotebook()) return;
    if (!hit) return;
    if (e.button === 2) {
      // Right-click uses a block (open chest/furnace/table, toggle door) unless
      // sneaking, which forces a place onto it (M6).
      if (!game.input.sneak && game.useBlock(hit.x, hit.y, hit.z)) return;
      // Held item used on the block: hoe→till, seed→plant, bonemeal→grow (M11.1).
      if (game.useItemOnBlock(hit.x, hit.y, hit.z, hit.nx, hit.ny, hit.nz)) return;
      const px = hit.x + hit.nx, py = hit.y + hit.ny, pz = hit.z + hit.nz;
      if (game.useHeldItem(px, py, pz)) return; // flint & steel → ignite portal (M7.1)
      game.placeBlock(px, py, pz);
    }
  });
  document.addEventListener('mouseup', (e) => {
    if (e.button === 0) game.breaking = false;
    if (e.button === 2) game.usingItem = false;
  });
}

/** Middle-click pick-block: put the block's item on the hotbar (D8/D16). */
function pickBlock(game: Game, state: number): void {
  const name = game.registries.states.blockName[state];
  if (!name || name === 'air') return;
  const itemId = game.registries.items.idOf.get(name);
  if (itemId === undefined) return;
  const pinv = game.playerState.inventory;
  const existing = pinv.find(itemId);
  if (existing >= 0 && existing < 9) { pinv.selected = existing; return; }
  const target = pinv.heldSlot();
  if (modeCaps(game.playerState.gameMode).infiniteResources || isEmpty(pinv.get(target))) {
    pinv.set(target, makeStack(itemId, 1));
  }
}
